import { useContext, createContext, useEffect, useState } from "react";
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, deleteUser } from "firebase/auth";
import { auth, db } from './firebase';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from "firebase/firestore";

// Create context
const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);

    // Google sign-in function
    const googleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const email = result.user.email;

            // Check if email is from the university
            if (!email.endsWith('@stu.kau.edu.sa') && !email.endsWith('@kau.edu.sa') && !email.endsWith('meng9977@gmail.com')) {
                await deleteUser(result.user);
                setError('Use your university email to login \nYou will still be able to browse the website in guest mode');
                setUser(null);
                return;
            }

            // Check if user exists in Firestore
            const userQuery = query(collection(db, 'users'), where('email', '==', email));
            const userSnapshot = await getDocs(userQuery);

            if (userSnapshot.empty) {
                // Add new user to Firestore if not found
                await addDoc(collection(db, 'users'), {
                    name: result.user.displayName,
                    email: result.user.email,
                    joined: new Date(),
                    projects: []
                });
            } 
            const userDoc = userSnapshot.docs[0].data();
            setUser(result.user);
            localStorage.setItem('user', JSON.stringify(result.user));
            setError('');

            // Check if user is an admin from Firestore
            const isAdmin = await checkIfUserIsAdmin(result.user);
            setIsAdmin(isAdmin);
            localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
          
        } catch (err) {
            console.error('Error signing in:', err);
            setError('Error signing in');
            setUser(null);
        }
    };

    // Log out function
    const logOut = () => {
        signOut(auth).then(() => {
            setUser(null);
            setIsAdmin(false);
            localStorage.removeItem('user');
            localStorage.removeItem('isAdmin');
        }).catch(err => {
            console.error('Error signing out:', err);
            setError('Error signing out');
        });
    };

    // Clear error
    const clearError = () => {
        setError('');
    };

    // Check if the user is an admin
    const checkIfUserIsAdmin = async (currentUser) => {
        try {
            // Query the users collection using the email instead of UID
            const userQuery = query(collection(db, 'users'), where('email', '==', currentUser.email));
            const querySnapshot = await getDocs(userQuery);
    
            if (!querySnapshot.empty) {
                const userDoc = querySnapshot.docs[0];  // Get the first document
                const userData = userDoc.data();  // Extract the data
                return userData.isAdmin || false;  // Return true if isAdmin is true, otherwise return false
            } else {
                console.log('User document not found.');
                return false;
            }
        } catch (error) {
            console.error('Error checking admin status:', error);
            return false;
        }
    };

   



const saveAdminStatusToLocalStorage = async () => {
    if (user) {
        try {
            // Query Firestore to get user admin status
            const userQuery = query(collection(db, 'users'), where('email', '==', user.email));
            const querySnapshot = await getDocs(userQuery);

            if (!querySnapshot.empty) {
                const userData = querySnapshot.docs[0].data();

                // Save admin status to localStorag
                localStorage.setItem('subUnit', JSON.stringify(userData['sub-unit'].id));
                localStorage.setItem('isAdmin', JSON.stringify(userData.isAdmin));
                localStorage.setItem('isLeader', JSON.stringify(userData.isLeader));
            }
        } catch (error) {
            console.error('Error checking admin status:', error);
        }
    }
};

// Call this function after the user logs in successfully
saveAdminStatusToLocalStorage();

    

    // Check authentication state and admin status on mount
    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const storedIsAdmin = localStorage.getItem('isAdmin');

        if (storedUser) {
            setUser(storedUser);
            setIsAdmin(storedIsAdmin === 'true');
        }

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                const isAdmin = await checkIfUserIsAdmin(currentUser);

                localStorage.setItem('user', JSON.stringify(currentUser));
                localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
                setUser(currentUser);
                setIsAdmin(isAdmin);
            } else {
                localStorage.removeItem('user');
                localStorage.removeItem('isAdmin');
                setUser(null);
                setIsAdmin(false);
            }
        });

        return () => unsubscribe();
    }, []);

    return (
        <AuthContext.Provider value={{ googleSignIn, logOut, user, isAdmin, error, clearError }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to access AuthContext
export const UserAuth = () => {
    return useContext(AuthContext);
};

// Fetch admin password from Firestore
export const fetchAdminPassword = async () => {
    try {
        const adminDocRef = doc(db, 'Administrator', 'd3qEpEO4RYyj9ybZDeTi'); // Ensure this document exists in Firestore
        const adminDoc = await getDoc(adminDocRef);

        if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            return adminData.adminPass;  // Ensure this field is present in your Firestore schema
        } else {
            throw new Error('Admin document not found.');
        }
    } catch (error) {
        console.error("Error fetching admin password:", error);
        throw error;
    }
};
